import { ErrorBoundary } from '@/components/Rollbar';
import {
  useDefaultPagination,
  useInvoicesTransaction,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoicesTransaction';
import { useTranslation } from '@/i18n';

import { Wrapper } from '@/components/OpenFeature/Wrapper';
import { useGetTransactionProperties } from '@/context/services/invoice_report/TransactionProperties.service';
import { useInvoiceTransactionColumns } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import { FC, memo, Suspense, useMemo } from 'react';
import { ErrorTransactionsDetailTable } from './ErrorTransactionsDetailTable';
import { LoadingTransactionsDetailTable } from './LoadingTransactionsDetailTable';
import {
  InternalTableProps,
  TransactionsDetailTable,
} from './TransactionsDetailTable';

type InnerListProps = {
  reportId: string;
  /** Column dataIndex values to exclude from the transactions table */
  excludeColumns?: string[];
  /** Render foreign-currency amounts with `AmountWithCurrencyCell` — approving_invoice_reports only */
  showCurrencyCell?: boolean;
};

const InnerList: FC<InnerListProps> = memo(
  ({ reportId, excludeColumns, showCurrencyCell }) => {
    const { data } = useInvoicesTransaction(reportId);
    // Fetch fields that will be used to generate dynamic columns
    const { data: dynamicTransactionFields } =
      useGetTransactionProperties(reportId);

    const columns = useInvoiceTransactionColumns(
      reportId,
      data ?? [],
      dynamicTransactionFields,
      showCurrencyCell
    );

    const filteredColumns = useMemo(() => {
      if (!excludeColumns || excludeColumns.length === 0) return columns;
      return columns.filter((col) => {
        if (typeof col.dataIndex === 'string') {
          return !excludeColumns.includes(col.dataIndex);
        }
        return true;
      });
    }, [columns, excludeColumns]);

    const { t } = useTranslation();
    const pagination = useDefaultPagination();
    const table: InternalTableProps = useMemo(() => {
      return {
        data: data ?? [],
        columns: filteredColumns ?? [],
        emptyText: <>{t('transactions_detail_empty_message')}</>,
      };
    }, [data, filteredColumns, t]);

    return <TransactionsDetailTable table={table} pagination={pagination} />;
  }
);
InnerList.displayName = 'InnerList';

type TransactionsDetailProps = {
  reportId: string;
  /** Column dataIndex values to exclude from the transactions table */
  excludeColumns?: string[];
  /** Render foreign-currency amounts with `AmountWithCurrencyCell` — approving_invoice_reports only */
  showCurrencyCell?: boolean;
};

export const TransactionsDetail: FC<TransactionsDetailProps> = memo(
  ({ reportId, excludeColumns, showCurrencyCell }) => {
    return (
      <Wrapper isProto={false}>
        <ErrorBoundary fallback={ErrorTransactionsDetailTable}>
          <Suspense fallback={<LoadingTransactionsDetailTable />}>
            <InnerList
              reportId={reportId}
              excludeColumns={excludeColumns}
              showCurrencyCell={showCurrencyCell}
            />
          </Suspense>
        </ErrorBoundary>
      </Wrapper>
    );
  }
);
TransactionsDetail.displayName = 'TransactionsDetail';
